import { useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import dayjs from "dayjs";
import {
  useProfile,
  useEvents,
  useReservations,
  useNeighborhoods,
  useSearchCities,
} from "@/lib/api";
import { UserAPI } from "@shared/types/api";
import { BOOKING_CUTOFF_HOURS } from "@shared/constants";
import { eventTimeToDaysjs, parseUTCDate, formatTimeToDisplay } from "@/utils/dateUtils";
import {
  getEventTypeEmoji,
  getEventTypeLabel,
  getEventTypeNoun,
} from "@/utils/eventTypeUtils";
import { getStoredAdEventType } from "@/lib/quizEventType";
import { useAppNavigation } from "@/lib/platform";
import { useAuth } from "@/lib/platform/AuthProvider";
import { useT } from "@/lib/i18n";
import tracking from "@/lib/tracking";
import { DateBadge } from "@/components/ux/DateBadge";
import { Loader } from "@/components/Loader";
import { pickGuidedPhotoStack, resolveGuidedAudience } from "../lib/guidedBookingPhoto";

/** Card-stack colors per offered event type — same palette family as the
 *  quiz interstitials so the screen reads as part of onboarding. */
const TYPE_STYLES: Record<string, { bgFrom: string; bgTo: string; ring: string }> = {
  coffee: { bgFrom: "#FEF3EE", bgTo: "#FDE3D8", ring: "#EF6225" },
  dinner: { bgFrom: "#F9F0FA", bgTo: "#F2E0F4", ring: "#81358A" },
  brunch: { bgFrom: "#FEF3EE", bgTo: "#F9F0FA", ring: "#F47D4D" },
  drinks: { bgFrom: "#EDFAFB", bgTo: "#D6F3F6", ring: "#36A2AD" },
};

/**
 * Guided post-signup booking screen (experiment guided_post_signup_booking).
 *
 * Rendered right after the quiz saves for users acquired via a typed ad
 * landing (coffee/dinner, persisted in localStorage). Styled as a
 * continuation of the onboarding stepper — same progress bar, motion and
 * glass-CTA language — and steers straight into booking the next event of
 * the user's ad type, with escape hatches to the full booking page and the
 * dashboard. Falls back to the next event of any type when none of the ad
 * type is bookable, and to /booking when nothing is bookable at all.
 */
export default function BookingStart() {
  const { t } = useT();
  const { navigate } = useAppNavigation();
  const { user: authUser } = useAuth();
  const adType = getStoredAdEventType();

  const { data: profileData, isLoading: profileLoading } = useProfile();
  const { data: eventsData, isLoading: eventsLoading } = useEvents();
  const { data: reservationsData } = useReservations();

  const cityId = profileData?.data?.user?.city;
  const { data: neighborhoodsData, isLoading: neighborhoodsLoading } =
    useNeighborhoods(undefined, cityId || undefined);
  const { data: citiesData } = useSearchCities("", cityId || undefined);
  const cityName = (citiesData?.data || []).find((c) => c.id === cityId)?.name;

  // Same bookability rules as BookingSection: inside the booking window, not
  // already reserved, and valid for a launched neighborhood of the user's city.
  const upcomingEvents = useMemo(() => {
    const neighborhoods = neighborhoodsData?.data || [];
    const activeReservationIds = new Set(
      (reservationsData?.data || [])
        .filter((r: UserAPI.Reservation) => r.status === "active")
        .map((r: UserAPI.Reservation) => r.eventTimeId),
    );
    return (eventsData?.data || []).filter((event) => {
      const beforeCutoff = eventTimeToDaysjs(event)
        .subtract(BOOKING_CUTOFF_HOURS, "hours")
        .isAfter(dayjs());
      if (!beforeCutoff || activeReservationIds.has(event.id)) return false;
      if (neighborhoods.length === 0) return false;
      return neighborhoods.some((n: any) => {
        if (event.neighborhoodIds && !event.neighborhoodIds.includes(n.id))
          return false;
        if (!n.launchedAfter) return true;
        return event.date > n.launchedAfter;
      });
    });
  }, [eventsData?.data, reservationsData?.data, neighborhoodsData?.data]);

  const nextOfAdType = adType
    ? upcomingEvents.find((e: any) => e.type === adType)
    : undefined;
  const targetEvent = nextOfAdType ?? upcomingEvents[0];
  const isLoading = profileLoading || eventsLoading || neighborhoodsLoading;

  // Nothing bookable at all — the booking page owns the empty/city-unlock state.
  const bailTracked = useRef(false);
  useEffect(() => {
    if (isLoading || targetEvent || bailTracked.current) return;
    bailTracked.current = true;
    tracking.guidedBookingUnavailable(adType ?? "none", cityId);
    navigate("/booking");
  }, [isLoading, targetEvent, adType, cityId, navigate]);

  const viewTracked = useRef(false);
  useEffect(() => {
    if (isLoading || !targetEvent || viewTracked.current) return;
    viewTracked.current = true;
    tracking.trackFlow("booking_start");
    tracking.guidedBookingViewed({
      adEventType: adType ?? "none",
      shownEventType: targetEvent.type ?? "dinner",
      isFallback: !!adType && !nextOfAdType,
      eventId: targetEvent.id,
      daysUntilEvent: eventTimeToDaysjs(targetEvent).diff(dayjs(), "day"),
      city: cityId,
    });
  }, [isLoading, targetEvent, adType, nextOfAdType, cityId]);

  if (isLoading || !targetEvent) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  const shownType = targetEvent.type || "dinner";
  const style = TYPE_STYLES[shownType] ?? TYPE_STYLES.dinner;
  const copyContext = shownType === "dinner" ? undefined : shownType;
  const parsedDate = parseUTCDate(targetEvent.date);
  const firstName = authUser?.firstName?.trim();

  const profile = profileData?.data?.profile;
  const age = profile?.birthday ? dayjs().diff(dayjs(profile.birthday), "year") : null;
  const [photoSrc, backLeftSrc, backRightSrc] = pickGuidedPhotoStack(
    shownType,
    resolveGuidedAudience(profile),
    age,
  );

  const trackCta = (action: "book" | "browse" | "skip") =>
    tracking.guidedBookingCtaClicked({
      adEventType: adType ?? "none",
      action,
      shownEventType: shownType,
      eventId: targetEvent.id,
    });

  const handleBook = () => {
    trackCta("book");
    const city = cityId ? `&city=${encodeURIComponent(cityId)}` : "";
    navigate(`/booking?eventId=${targetEvent.id}${city}`);
  };
  const handleBrowse = () => {
    trackCta("browse");
    navigate("/booking");
  };
  const handleSkip = () => {
    trackCta("skip");
    navigate("/dashboard");
  };

  return (
    <div className="h-full min-h-0 w-full flex justify-center relative overflow-hidden">
      <div className="relative w-full max-w-[480px] h-full min-h-0 flex flex-col">
        {/* Top bar — mirrors the quiz stepper so this reads as its final step */}
        <div className="top-safe-padding-4 pb-2 px-4 flex items-center">
          <div className="w-10 h-10" />
          <h1 className="flex-1 text-center text-[11px] tracking-[0.22em] uppercase font-semibold text-neutral-700 dark:text-neutral-400">
            {t("guidedBooking.kicker")}
          </h1>
          <div className="w-10 h-10" />
        </div>

        {/* Progress bar, nearly full — booking is the last step of onboarding */}
        <div className="px-4 mb-3">
          <div className="h-[3px] w-full rounded-full overflow-hidden bg-black/10 dark:bg-white/10">
            <motion.div
              className="h-full rounded-full"
              style={{
                background:
                  "linear-gradient(to right, #EF6225 0%, #B868C0 60%, #36A2AD 100%)",
                boxShadow:
                  "0 0 10px rgba(239,98,37,0.6), 0 0 20px rgba(184,104,192,0.3)",
              }}
              initial={{ width: "80%" }}
              animate={{ width: "96%" }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            />
          </div>
        </div>

        <div className="flex-1 min-h-0 flex flex-col px-5 pb-2 overflow-y-auto">
          <div className="flex-1 flex flex-col items-center justify-center py-4">
            {/* Card stack with a thematic photo (event type × audience × age)
                — same stack-and-ring visual as the quiz vibe-reveal
                interstitial, with the type gradient as the loading backdrop */}
            <div className="relative mb-8 w-48 h-60">
              {[
                {
                  src: backLeftSrc,
                  className: "rotate-[-9deg] -translate-x-7 scale-[0.94]",
                },
                {
                  src: backRightSrc,
                  className: "rotate-[9deg] translate-x-7 scale-[0.94]",
                },
              ].map((card) => (
                <div
                  key={card.className}
                  className={`absolute inset-0 rounded-3xl overflow-hidden bg-neutral-100 dark:bg-neutral-900 shadow-lg transform ${card.className}`}
                >
                  <img
                    src={card.src}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  {/* Knock the back cards back so the front photo stays dominant */}
                  <div className="absolute inset-0 bg-white/25 dark:bg-black/45" />
                </div>
              ))}
              <motion.div
                initial={{ opacity: 0, scale: 0.92, y: 12 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.45, ease: "easeOut" }}
                className="absolute inset-0 rounded-3xl flex items-center justify-center overflow-hidden"
                style={{
                  background: `linear-gradient(180deg, ${style.bgFrom} 0%, ${style.bgTo} 100%)`,
                  boxShadow: `0 16px 40px rgba(0,0,0,0.18), inset 0 0 0 2px ${style.ring}`,
                }}
              >
                <img
                  src={photoSrc}
                  alt=""
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <motion.div
                  className="absolute top-6 right-8 text-2xl"
                  animate={{ scale: [1, 1.3, 1], opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  ✨
                </motion.div>
                <motion.div
                  className="absolute bottom-9 left-8 text-xl"
                  animate={{ scale: [1, 1.2, 1], opacity: [0.6, 1, 0.6] }}
                  transition={{ duration: 2.4, repeat: Infinity, delay: 0.6 }}
                >
                  ✨
                </motion.div>
              </motion.div>
            </div>

            <motion.h2
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.35 }}
              className="text-3xl font-bold text-type-900 dark:text-neutral-100 text-center mb-3 leading-tight"
            >
              {firstName
                ? t("guidedBooking.titleNamed", { name: firstName, context: copyContext })
                : t("guidedBooking.title", { context: copyContext })}
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.35 }}
              className="text-base text-neutral-600 dark:text-neutral-400 text-center max-w-md leading-relaxed mb-6"
            >
              {t("guidedBooking.subtitle", { context: copyContext })}
            </motion.p>

            {/* Fallback note when the ad type has no bookable date */}
            {adType && !nextOfAdType && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-sm text-neutral-500 dark:text-neutral-400 text-center mb-4"
              >
                {t("guidedBooking.fallbackNote", {
                  adType: getEventTypeNoun(adType),
                  shownType: getEventTypeNoun(shownType),
                })}
              </motion.p>
            )}

            {/* Next event card */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.35 }}
              className="w-full max-w-sm rounded-2xl border border-black/10 dark:border-white/10 bg-white/70 dark:bg-white/5 backdrop-blur-md p-4"
            >
              <div className="text-[11px] tracking-[0.18em] uppercase font-semibold text-neutral-700 dark:text-neutral-400 mb-3">
                {cityName
                  ? t("guidedBooking.nextUpIn", { city: cityName })
                  : t("guidedBooking.nextUp")}
              </div>
              <div className="flex items-center gap-4">
                <DateBadge date={targetEvent.date} size="xl" />
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-md font-semibold leading-tight">
                    <span>{getEventTypeEmoji(shownType)}</span>
                    <span>{getEventTypeLabel(shownType)}</span>
                  </div>
                  <div className="text-sm text-neutral-600 dark:text-neutral-300 mt-0.5">
                    {parsedDate.weekday} · {formatTimeToDisplay(targetEvent.time)}
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Escape hatches live directly under the event card: the sticky
                dock below sits above the floating mobile nav-bar, and parking
                them there put them under it. */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55, duration: 0.3 }}
              className="flex items-center justify-center gap-6 mt-4"
            >
              <button
                onClick={handleBrowse}
                className="text-sm font-medium text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors py-2"
              >
                {t("guidedBooking.browseCta")}
              </button>
              <span className="text-neutral-300 dark:text-neutral-600">·</span>
              <button
                onClick={handleSkip}
                className="text-sm font-medium text-neutral-600 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors py-2"
              >
                {t("guidedBooking.skipCta")}
              </button>
            </motion.div>
          </div>
        </div>

        {/* CTA dock — glass gradient button matching the stepper's Continue */}
        <div className="sticky left-0 right-0 bottom-0 mt-auto px-4 pt-4 pb-above-nav-bar">
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.25 }}
          >
            <button
              onClick={handleBook}
              className="w-full py-4 rounded-full text-base font-semibold text-white active:scale-[0.99] transition-all relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #EF6225 0%, #B868C0 100%)",
                boxShadow:
                  "0 0 20px rgba(239,98,37,0.5), 0 0 10px rgba(184,104,192,0.3), inset 0 1px 0 rgba(255,255,255,0.25)",
              }}
            >
              {t("guidedBooking.bookCta")}
            </button>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
